import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { BlogPost } from "@/lib/blog-api";

export const POSTS_PER_PAGE = 9;

interface UseBlogPostsParams {
  page?: number;
  search?: string;
  category?: string | null;
  tag?: string | null;
  enabled?: boolean;
}

export interface BlogListResult {
  posts: BlogPost[];
  totalCount: number;
  totalPages: number;
}

/** Paginated, searchable, filterable blog list using server-side full-text search. */
export function useBlogPosts({ page = 1, search = "", category = null, tag = null, enabled = true }: UseBlogPostsParams) {
  return useQuery<BlogListResult>({
    queryKey: ["blog-posts", page, search, category, tag],
    enabled,
    queryFn: async () => {
      const offset = (page - 1) * POSTS_PER_PAGE;
      const trimmedSearch = search.trim();

      // Use full-text search RPC for both search and listing (handles category filter too)
      let postsRes, countRes;

      if (tag) {
        // Fallback to client-side filtering if searching by tag, since RPC doesn't support it directly
        const { data, error } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("is_published", true)
          .order("published_at", { ascending: false });

        if (error) throw error;

        let filtered = data || [];
        if (category) filtered = filtered.filter(p => p.category === category);
        if (trimmedSearch) {
          const s = trimmedSearch.toLowerCase();
          filtered = filtered.filter(p =>
            p.title.toLowerCase().includes(s) ||
            (p.excerpt && p.excerpt.toLowerCase().includes(s))
          );
        }
        filtered = filtered.filter(p => p.tags && Array.isArray(p.tags) && p.tags.includes(tag));

        countRes = { data: filtered.length, error: null };
        postsRes = { data: filtered.slice(offset, offset + POSTS_PER_PAGE), error: null };
      } else {
        const [pRes, cRes] = await Promise.all([
          supabase.rpc("search_blog_posts", {
            search_query: trimmedSearch,
            category_filter: category,
            limit_count: POSTS_PER_PAGE,
            offset_count: offset,
          }),
          supabase.rpc("count_blog_posts", {
            search_query: trimmedSearch,
            category_filter: category,
          }),
        ]);
        postsRes = pRes;
        countRes = cRes;
      }

      if (postsRes.error) throw postsRes.error;
      if (countRes.error) throw countRes.error;

      const postIds = (postsRes.data || []).map((p: any) => p.author_id).filter(Boolean) as string[];
      const uniqueAuthorIds = Array.from(new Set(postIds));

      // Fetch author profiles in batch
      let authorMap = new Map<string, { full_name: string | null; avatar_url: string | null }>();
      if (uniqueAuthorIds.length > 0) {
        const { data: authors } = await supabase
          .from("public_profiles")
          .select("user_id, full_name, avatar_url")
          .in("user_id", uniqueAuthorIds);
        authorMap = new Map(
          (authors || []).map((a) => [a.user_id, { full_name: a.full_name, avatar_url: a.avatar_url }])
        );
      }

      const posts: BlogPost[] = (postsRes.data || []).map((p: any) => ({
        ...p,
        author: p.author_id ? authorMap.get(p.author_id) || null : null,
      }));

      const totalCount = Number(countRes.data || 0);
      return {
        posts,
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / POSTS_PER_PAGE)),
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60, // 1 min
  });
}

/** Single post by slug. */
export function useBlogPost(slug: string | undefined) {
  return useQuery<BlogPost | null>({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // Fetch author separately
      let author = null;
      if (data.author_id) {
        const { data: profile } = await supabase
          .from("public_profiles")
          .select("full_name, avatar_url")
          .eq("user_id", data.author_id)
          .maybeSingle();
        author = profile;
      }

      return { ...data, author } as BlogPost;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });
}

/** Posts related to a given post (same category or shared tags), excluding current. */
export function useRelatedPosts(post: BlogPost | null | undefined, limit = 3) {
  return useQuery<BlogPost[]>({
    queryKey: ["blog-related", post?.id, post?.category, post?.tags?.join(",")],
    queryFn: async () => {
      if (!post) return [];
      let query = supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .neq("id", post.id)
        .order("published_at", { ascending: false })
        .limit(limit);

      if (post.category) {
        query = query.eq("category", post.category);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fallback: if category yields fewer than limit, top up with most recent
      if ((data?.length || 0) < limit) {
        const { data: extras } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("is_published", true)
          .neq("id", post.id)
          .order("published_at", { ascending: false })
          .limit(limit);
        const existingIds = new Set((data || []).map((d) => d.id));
        const merged = [...(data || []), ...(extras || []).filter((e) => !existingIds.has(e.id))];
        return merged.slice(0, limit) as BlogPost[];
      }

      return (data || []) as BlogPost[];
    },
    enabled: !!post,
    staleTime: 1000 * 60 * 5,
  });
}

/** All distinct categories from published posts (for filter chips). */
export function useBlogCategories() {
  return useQuery<string[]>({
    queryKey: ["blog-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("category")
        .eq("is_published", true)
        .not("category", "is", null);
      if (error) throw error;
      const set = new Set<string>();
      (data || []).forEach((row: any) => row.category && set.add(row.category));
      return Array.from(set).sort();
    },
    staleTime: 1000 * 60 * 10,
  });
}

/** Increment view count via RPC (fire-and-forget). */
export async function incrementBlogView(slug: string) {
  try {
    await supabase.rpc("increment_blog_view", { post_slug: slug });
  } catch (err) {
    console.warn("Failed to increment blog view:", err);
  }
}
